import {Capacitor} from '@capacitor/core';
import {TextZoom} from '@capacitor/text-zoom';

/**
 * Lets the system text size scale the app (WCAG 1.4.4), capped at 200%.
 *
 * Everything that should scale is sized in rem: Ionic's components, the Tailwind
 * spacing and the app's type scale (see --font-unit in theme/variables.css). So the
 * root font size is the one knob, and this keeps it following the system setting on
 * both platforms. The fixed px chrome of Ionic's iOS mode (tab bar, segments, header
 * titles) stays put, the way native iOS bars do.
 *
 * iOS: Ionic roots the font on Apple's Body text style (see typography.css in
 * @ionic/core), 17px at the default size, and WebKit keeps it in sync with Dynamic
 * Type, also while the app is open. The accessibility sizes go up to about 310%, which
 * leaves a few words per line on a phone, so cap the root at 200%. A hidden probe on
 * the same text style tells when the system size changes.
 *
 * Android: the WebView applies the system font scale as text zoom instead, which
 * scales every font size including px ones: the tab bar icons and labels outgrow the
 * fixed bar, and no cap holds since the cap is scaled too. So turn text zoom off and
 * scale the root the same way. Changing the system font size restarts the activity
 * (fontScale is not in android:configChanges), so reading it at startup is enough.
 *
 * Both flag larger than default text with a class on <html>, for layout tweaks that
 * should not touch the default size (see styles/typography.css).
 */

const MAX_SCALE = 2;
const IOS_DEFAULT_ROOT = 17;
const ANDROID_DEFAULT_ROOT = 16;

const root = document.documentElement;

const flagScaled = (scaled: boolean) => {
  root.classList.toggle('text-scaled', scaled);
};

const capIosRoot = () => {
  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;height:0;width:1em;font:-apple-system-body';
  document.body.appendChild(probe);

  const maxSize = IOS_DEFAULT_ROOT * MAX_SCALE;
  const update = () => {
    const systemSize = probe.getBoundingClientRect().width;
    root.style.fontSize = systemSize > maxSize ? `${maxSize}px` : '';
    flagScaled(systemSize > IOS_DEFAULT_ROOT);
  };
  update();
  new ResizeObserver(update).observe(probe);
};

const scaleAndroidRoot = async () => {
  const {value} = await TextZoom.getPreferred();
  await TextZoom.set({value: 1});
  root.style.fontSize = `${ANDROID_DEFAULT_ROOT * Math.min(value, MAX_SCALE)}px`;
  flagScaled(value > 1);
};

export const applyTextScale = async () => {
  try {
    if (Capacitor.getPlatform() === 'ios') {
      capIosRoot();
    } else if (Capacitor.getPlatform() === 'android') {
      await scaleAndroidRoot();
    }
  } catch (e) {
    console.warn('Text scale setup failed', e);
  }
};
